import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { API } from 'api';
import { Alert, AlertServer } from 'features/alerts/alerts.types';
import { safeParseDate } from 'shared/dates';
import { getCentroid } from 'features/mapping/mapping.utils';

type UseAlertProps = {
  id?: number;
};

type UseAlert = Omit<UseQueryResult<Alert, Error>, 'data'> & {
  alert?: Alert;
};

const fetchAlert = async (alertId: number | string): Promise<Alert> => {
  const response = await API.get<AlertServer>(
    `warnings_and_alerts/${alertId}/`,
  );
  return {
    ...response.data,
    dateIssued: safeParseDate(response.data.dateIssuedInSource) ?? null,
    dateExpires: safeParseDate(response.data.dateExpiresInSource) ?? null,
    dateOnset: safeParseDate(response.data.dateOnsetInSource) ?? null,
    dateEnds: safeParseDate(response.data.dateEndsInSource) ?? null,
    dateLastUpdated:
      safeParseDate(response.data.dateLastUpdatedInSource) ?? null,
    // Needed to center the map on the selected alert
    center: getCentroid(response.data.geom),
  };
};

export const useAlert = (props: UseAlertProps): UseAlert => {
  const { id } = props;

  const { data, ...results } = useQuery({
    queryKey: ['alert', id],
    queryFn: () => fetchAlert(id!),
    enabled: Boolean(id),
  });

  return { ...results, alert: data };
};
